import React, { PureComponent, ReactNode } from "react";
import Draggable from "react-draggable";
import { PropertyDataType } from "src/backend/constants/datatypes";
import { IDragAndDropConfigurationItem, IEditPropertyValue, IObjectTypeConfigurationItem, IOwnPropertyOrId, IWorkflow } from "src/backend/interfaces/configuration/IConfiguration";
import { IItem } from "src/backend/interfaces/IItem";
import IMFilesPropertyDefinition from "src/backend/interfaces/mfiles/IMFilesPropertyDefinition";
import { formatNumeric, parseNumeric } from "src/gantt/helpers/NumberHelpers";
import { formatWithLocale as formatDate, parseDate } from "src/gantt/helpers/DateHelpers";
import { IObjectUpdate } from "src/rematch/interfaces/IObjectUpdate";
import styled from "styled-components";
import { GenericButton, SecondaryButton } from "./Buttons";
import { parseISO } from "date-fns";
import { WithTranslation } from "react-i18next";
import { translatePropertyDefName, translateStateName, translateValueListItemName } from "src/backend/helpers/translationHelpers";
import { ModalBackdrop, ModalContainer, styledScrollBars } from "./StyledComponents";
import IMfilesValueListDefinition from "src/backend/interfaces/mfiles/IMFilesValueListDefinition";
import IMfilesValueListItem from "src/backend/interfaces/mfiles/IMfilesValueListItem";
import { BuiltInPropertyDef } from "src/backend/constants/builtinProperties";
import { getLookupItemsAsync, getValueListItemsAsync } from "src/backend/services/FilterDataFetchService";
import { AxiosInstance } from "axios";
import { EditValueListProperty, SelectOption } from "./EditValueListProperty";
import { EditStringProperty } from "./EditStringProperty";
import { IObjectInsert } from "src/rematch/interfaces/IObjectInsert";
import { MFILES_DATE_VALUE_FORMAT, MFILES_INT_MAX, MFILES_INT_MIN } from "src/backend/constants/common";
import { IMfilesObjectClass } from "src/backend/interfaces/mfiles/IMfilesObjectClass";
import { mergeDateAndTime } from "src/backend/helpers/dateHelpers";
import EditDateProperty from "./EditDateProperty";

export const DRAG_HANDLE_CLASSNAME = "dragHandle";

const ITEMFORM_TOP = 190;
const ITEMFORM_LEFT = 520;
const ITEMFORM_HEADER_WIDTH = 200;
const ITEMFORM_VALUE_WIDTH = 310;

export const ItemFormContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: left;
  position: fixed;

  padding-left: 23px;
  padding-right: 23px;
  padding-top: 10px;

  top: ${ITEMFORM_TOP}px;
  left: ${ITEMFORM_LEFT}px;
  
  background: #ffffff;
  border: 2px solid #4eab00;
  box-sizing: border-box;
  box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.25);
`;

export const ItemFormHeader = styled.div`
  width: 100%;
  height: 24px;

  margin-bottom: 10px;

  font-size: 18px;
  line-height: 24px;
  text-align: start;

  color: #525252;
  text-transform: uppercase;
`;

export const ItemFormHeaderContainer = styled.div`
  width: 100%;
`;

export const ItemFormPropertiesContainer = styled.div`
  max-height: ${window.innerHeight - ITEMFORM_TOP - 132}px;
  overflow-y: auto;
  ${styledScrollBars}
`;

export const ItemFormButtonRow = styled.div`
  flex: 0 1 auto;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

export const DragHandle = styled.div.attrs(() => ({
  className: DRAG_HANDLE_CLASSNAME,
}))`
  cursor: move;
  width: 100%;
  height: 100%;
`;

export const ItemFormColumnsOuter = styled.table`
  border-collapse: collapse;
`;

export const ItemFormColumnsInner = styled.tbody`
  font-family: Segoe UI;
  font-size: 14px;
`;

export const ItemFormColumn = styled.tr`
`;

export const ItemFormColumnHeader = styled.td`
  width: ${ITEMFORM_HEADER_WIDTH}px;
  text-align: right;
  font-weight: bold;
`;

export const ItemFormColumnValue = styled.td`
  min-width: ${ITEMFORM_VALUE_WIDTH}px;
  max-width: ${Math.min(500, window.innerWidth - ITEMFORM_LEFT - ITEMFORM_HEADER_WIDTH - 50)}px;
  padding-left: 5px;
`;

export interface IItemFormProps extends WithTranslation {

  //connect
  httpClient: AxiosInstance;
  selectedItem: number;
  getItem: (itemId: number) => IItem;
  getEditProperties: (objectType: number) => IEditPropertyValue[];
  getPropertyDefinition: (propertyId: number) => IMFilesPropertyDefinition;
  getValueListDefinition: (valueListId: number) => IMfilesValueListDefinition;
  getValueListItems: (valueListId: number) => IMfilesValueListItem[];
  getWorkflow: (workflowId: number) =>  IWorkflow;
  getClass: (classId: number) => IMfilesObjectClass;
  objectTypeConfigurations: Map<number,IObjectTypeConfigurationItem>;
  dragndropConfigurations: IDragAndDropConfigurationItem[];
  valueListItems: IMfilesValueListItem[][];
  dateFormat: string,
  timeFormat: string,
  showTimeInTable: boolean,

  toggleShowForm: () => void;
  updateManyAsync: (updates: IObjectUpdate[]) => void;
  insertItemAsync: (insert: IObjectInsert) => void;
  setValueListItems: (valueListId: number, valueListItems: IMfilesValueListItem[]) => void;
}

export interface IItemFormState {
  item       : IItem;
  properties : IItemPropertyDetails[];
}

export interface IItemPropertyDetails {
  readOnly         : boolean;
  mandatory        : boolean;
  propertyIdOrName : IOwnPropertyOrId;
  isOwnProperty    : boolean;
  dataType         : PropertyDataType;
  value            : string;
  displayValue     : string;
  originalValue    : string;
  changed          : boolean;
  hasError         : boolean;
}

export class ItemFormCommon extends PureComponent<IItemFormProps, IItemFormState> {

  dragRef: React.RefObject<HTMLDivElement>;

  constructor(props: IItemFormProps) {
    super(props);
    this.dragRef = React.createRef();
    this.state = {
      item: null,
      properties: []
    }
  }

  getInitialProperties(item: IItem, insertMode: boolean): IItemPropertyDetails[] {

    const editProperties = this.props.getEditProperties(item?.objectType);
    const properties: IItemPropertyDetails[] = [];

    for(const editProperty of editProperties.filter(i => insertMode ? i.updateOnly === false : i.insertOnly === false )) {

      const isOwnProperty = typeof editProperty.propertyIdOrName === 'string';
      if(isOwnProperty) {
        const ownProperty = editProperty.propertyIdOrName as string;
        let ownPropertyDataType = PropertyDataType.Text;
        let ownPropertyValue: string = null;
        let ownPropertyDisplayValue: string = null;
        let ownPropertyReadOnly = editProperty.readOnly;
        let ownPropertyValid = false;
        let ownPropertyHasError = false;
        let ownPropertyChanged = false;
        if(ownProperty === "name") {
          ownPropertyDataType = PropertyDataType.Text;
          ownPropertyValue = item.name;
          ownPropertyDisplayValue = item.name;
          ownPropertyReadOnly = true;
          ownPropertyValid = true;
        }
        if(ownProperty === "start") {
          ownPropertyDataType     = PropertyDataType.Date;
          ownPropertyValue        = item.start ? formatDate(item.start, MFILES_DATE_VALUE_FORMAT) : '';
          ownPropertyDisplayValue = item.start ? formatDate(item.start, this.getDateFormat())     : '';
          ownPropertyValid        = true;
        }
        if(ownProperty === "end") {
          ownPropertyDataType     = PropertyDataType.Date;
          ownPropertyValue        = item.end ? formatDate(item.end, MFILES_DATE_VALUE_FORMAT) : '';
          ownPropertyDisplayValue = item.end ? formatDate(item.end, this.getDateFormat())     : '';
          ownPropertyValid        = true;
        }
        if(ownPropertyValid) {
          if(ownPropertyValue === null || ownPropertyValue === "") {
            if(editProperty.mandatory) {
              ownPropertyHasError = true;
            }
          } else if (insertMode) {
            ownPropertyChanged = true;
          }
          properties.push({
            readOnly         : ownPropertyReadOnly,
            mandatory        : editProperty.mandatory,
            propertyIdOrName : editProperty.propertyIdOrName,
            isOwnProperty    : isOwnProperty,
            dataType         : ownPropertyDataType,
            value            : ownPropertyValue,
            displayValue     : ownPropertyDisplayValue,
            originalValue    : insertMode ? "" : ownPropertyValue,
            changed          : ownPropertyChanged,
            hasError         : ownPropertyHasError,
          });
        }
      } else {
        const propertyId = editProperty.propertyIdOrName as number;
        let property = item.properties.find((f) => f.property === propertyId);
        if(!property) {
          const propertyDef = this.props.getPropertyDefinition(propertyId);
          property = {
            property     : propertyId,
            dataType     : propertyDef.DataType,
            value        : null,
            displayValue : ""
          };
        }
        let displayValue = property.displayValue;
        if(property.dataType === PropertyDataType.Date && property.value) {
          displayValue = formatDate(parseISO(property.value.slice(0, -1)), this.props.dateFormat);
        }
        if(property.dataType === PropertyDataType.Time && property.value) {
          displayValue = formatDate(parseISO(property.value.slice(0, -1)), this.props.timeFormat);
        }
        if(property.dataType === PropertyDataType.Boolean) {
          displayValue = property.value === null ? null : property.value ? "true" : "false";
        }
        if(property.dataType === PropertyDataType.Lookup || property.dataType === PropertyDataType.MultiSelectLookup) {
          const propertyDef = this.props.getPropertyDefinition(property.property);
          if(propertyDef.BasedOnValueList && propertyDef.ValueList) {
            const valueListDefinition = this.props.getValueListDefinition(propertyDef.ValueList);
            if(valueListDefinition.RealObjectType !== true) {
              const selection = this.getLookupSelection(property.value, property.displayValue);
              displayValue = selection.map(i => (translateValueListItemName(propertyDef.ValueList, parseInt(i.value), i.label))).join(";");
            }
          }
        }
        let hasChanged = false;
        let hasError = false;
        if(displayValue === null || displayValue === "") {
          if(editProperty.mandatory) {
            hasError = true;
          }
        } else if (insertMode) {
          hasChanged = true;
        }
        properties.push({
          readOnly         : editProperty.readOnly,
          mandatory        : editProperty.mandatory,
          propertyIdOrName : editProperty.propertyIdOrName,
          isOwnProperty    : isOwnProperty,
          dataType         : property.dataType,
          value            : property.value,
          displayValue     : displayValue,
          originalValue    : insertMode ? "" : property.value,
          changed          : hasChanged,
          hasError         : hasError,
        });
      }
    }
    return properties;
  }

  validatePropertyValue(property: IItemPropertyDetails, propertyValue: string): [boolean, string, string] {
    
    if( propertyValue === null || propertyValue === undefined || propertyValue === '' ) {
      return [!property.mandatory, null, ""];
    }

    switch( property.dataType ) {

      case PropertyDataType.Text:
      case PropertyDataType.MultilineText:

        return [true, propertyValue, propertyValue];

      case PropertyDataType.Floating: {
        
        const floatVal = parseNumeric(propertyValue);

        return isNaN(floatVal) ?
          [false, propertyValue, propertyValue] :
          [true, floatVal.toString(), floatVal.toString()];
      }
      case PropertyDataType.Integer: {

        const intReg   = /^-?\d*$/;
        const intValue = parseInt(propertyValue);
        const intValid = intReg.test(propertyValue) && intValue >= MFILES_INT_MIN && intValue <= MFILES_INT_MAX;
        
        return [intValid, intValue.toString(), intValue.toString()];
      }
      case PropertyDataType.Integer64: {
        const int64Reg   = /^-?\d*$/;
        const int64Value = parseInt(propertyValue);
        const int64Valid = int64Reg.test(propertyValue) && Number.isSafeInteger(int64Value);
        
        return [int64Valid, int64Value.toString(), int64Value.toString()];
      }
      case PropertyDataType.Date: {
        
        const dateFormat = property.isOwnProperty ? this.getDateFormat() : this.props.dateFormat;
        let dateValue    = parseDate(propertyValue, dateFormat);

        if( property.isOwnProperty && !this.props.showTimeInTable ) {

          const objectTypeConfig = this.props.objectTypeConfigurations.get(this.state.item.objectType);

          if( objectTypeConfig.timeDefaults && objectTypeConfig.timeDefaults.length === 2 ) {

            const ownProperty                        = property.propertyIdOrName as string;
            const [defaultStartTime, defaultEndTime] = objectTypeConfig.timeDefaults;

            if( ownProperty === "start" && defaultStartTime ) {
              dateValue = mergeDateAndTime(dateValue, defaultStartTime);
            }
            if( ownProperty === "end" && defaultEndTime ) {
              dateValue = mergeDateAndTime(dateValue, defaultEndTime);
            }
          }
        }

        const dateValid  = !isNaN(dateValue.getTime());
        const dateString = dateValid ? formatDate(dateValue, MFILES_DATE_VALUE_FORMAT) : '';
        
        return [dateValid, dateString, propertyValue];
      }
      case PropertyDataType.Time: {

        const timeValue  = parseDate(propertyValue, this.props.timeFormat);
        const timeValid  = !isNaN(timeValue.getTime());
        const timeString = timeValid ? formatDate(timeValue, MFILES_DATE_VALUE_FORMAT) : '';
        
        return [timeValid, timeString, propertyValue];
      }
      case PropertyDataType.Boolean:
        return [true, propertyValue, propertyValue];

      default:
        return [true, propertyValue, propertyValue];
    }
  }

  changePropertyValue(propertyIdOrName: IOwnPropertyOrId, propertyValue: string): void {

    this.setState((prevState) => {
      const properties: IItemPropertyDetails[] = [];

      for( const property of prevState.properties ) {

        if( property.propertyIdOrName === propertyIdOrName ) {

          const [ isValid, validatedValue, displayValue ] = this.validatePropertyValue(property, propertyValue);
          
          properties.push({
            readOnly         : property.readOnly,
            mandatory        : property.mandatory,
            propertyIdOrName : property.propertyIdOrName,
            isOwnProperty    : property.isOwnProperty,
            dataType         : property.dataType,
            value            : validatedValue,
            displayValue     : displayValue,
            originalValue    : property.originalValue,
            changed          : String(validatedValue) !== String(property.originalValue),
            hasError         : !isValid,
          });
          
        } else {
          properties.push(property);
        }
      }
      return {
        ...prevState,
        properties: properties,
      }
    });
  }

  getLookupSelection(propertyValue: string, displayValue: string): SelectOption[] {
    const selection: SelectOption[] = [];
    const values = propertyValue?.split(",");
    const labels = displayValue?.split(";").map(v => v.trim());

    for( let i = 0; i < values?.length; i++ ) {
      const value = values[i];
      const label = labels?.length > i ? labels[i] : "";
      selection.push({value: value, label: label});
    }
    return selection;
  }

  async getValuelistLookup(valuelistId: number, inputValue: string): Promise<SelectOption[]> {
    let results = this.props.getValueListItems(valuelistId);
    if(!results) {
      results = await getValueListItemsAsync(this.props.httpClient, valuelistId);
      this.props.setValueListItems(valuelistId, results);
    }
    return this.sortAndFilterSelectOptions(results.map(i => ({value: i.ID.toString(), label: translateValueListItemName(valuelistId, i.ID, i.Name)})), inputValue);
  }

  async getObjectLookup(objectTypeId: number, inputValue: string): Promise<SelectOption[]> {
    const results = await getLookupItemsAsync(this.props.httpClient, objectTypeId, inputValue, 40);
    return this.sortAndFilterSelectOptions(results.map(i => ({value: i.ObjVer.ID.toString(), label: i.Title})), inputValue);
  }

  getStateLookup(inputValue: string): Promise<SelectOption[]> {
    const lookupValues: SelectOption[] = [];
    let workflowId: number = null;
    const workflowProperty = this.state.item.properties.find(f => f.property === BuiltInPropertyDef.Workflow);
    if(workflowProperty) {
      workflowId = parseInt(workflowProperty.value);
    } else {
      //Item created without workflow, resolve workflow with class-property
      const classProperty = this.state.item.properties.find(f => f.property === BuiltInPropertyDef.Class);
      if(classProperty) { 
        const objectClass = this.props.getClass(parseInt(classProperty.value));
        if(objectClass) {
          workflowId = objectClass.Workflow;
        }
      }
    }
    if(workflowId) {
      const workflow = this.props.getWorkflow(workflowId);
      if(workflow?.States) {
        for(const state of workflow.States) {
          lookupValues.push({
            value: state.ID.toString(),
            label: translateStateName(state.ID, state.Name),
          });
        }
      }
    }
    return Promise.resolve(this.sortAndFilterSelectOptions(lookupValues, inputValue));
  }

  sortAndFilterSelectOptions(options: SelectOption[], inputValue: string): SelectOption[] {
    return options
      .sort((a,b) => a.label.trim().localeCompare(b.label.trim()))
      .filter((f) => inputValue === null || inputValue === "" || f.label.toLowerCase().includes(inputValue.toLowerCase()));
  }

  renderDisplayValue(property: IItemPropertyDetails) : ReactNode {
    const { t } = this.props;
    if( !property || property.value === null || property.value === undefined || property.value === "" ) {
      return "";
    }
    switch(property.dataType) {
      case PropertyDataType.Boolean:
        return t(`boolean.${property.value.toString()}`).toString();
      case PropertyDataType.Date:
        if(property.isOwnProperty) {
          const ownProperty = property.propertyIdOrName as string;
          if(ownProperty === "start") {
            return formatDate(this.state.item.start, this.getDateFormat());
          }
          if(ownProperty === "end") {
            return formatDate(this.state.item.end, this.getDateFormat());
          }
        }
        //remove trailing Z
        return formatDate(parseISO(property.value.slice(0, -1)), this.props.dateFormat);
      case PropertyDataType.Time:
        //remove trailing Z
        return formatDate(parseISO(property.value.slice(0, -1)), this.props.timeFormat);
      case PropertyDataType.Lookup:
      case PropertyDataType.MultiSelectLookup: {
        const propertyDefinition = this.props.getPropertyDefinition(property.propertyIdOrName as number);
        if(propertyDefinition && propertyDefinition.BasedOnValueList && propertyDefinition.ValueList !== null) {
          const valueListDefinition = this.props.getValueListDefinition(propertyDefinition.ValueList)
          if(valueListDefinition) {
            const i = 0;
            const translatedValues = [];
            for(const selected of this.getLookupSelection(property.value, property.displayValue)) {
              const translatedValue = translateValueListItemName(propertyDefinition.ValueList, parseInt(selected.value), selected.label);
              translatedValues.push(<div key={i}>{translatedValue}</div>);
            }
            return translatedValues;
          }
        }
        return property?.displayValue ?? "";
      }
      default:
        return property?.displayValue ?? "";
    }
  }

  renderProperty (property: IItemPropertyDetails): ReactNode {
    const { t } = this.props;
    if(property.readOnly) {
      return(this.renderDisplayValue(property));
    } else {
      switch(property.dataType) {
        case PropertyDataType.Text:
          return <EditStringProperty 
            propertyValue={property.value} 
            hasChanged={property.changed} 
            hasError={property.hasError} 
            multiLine={false} 
            onChange={(value) => this.changePropertyValue(property.propertyIdOrName, value)} />;
        case PropertyDataType.MultilineText:
          return <EditStringProperty 
            propertyValue={property.value} 
            hasChanged={property.changed} 
            hasError={property.hasError} 
            multiLine={true}  
            onChange={(value) => this.changePropertyValue(property.propertyIdOrName, value)} />;
        case PropertyDataType.Integer:
        case PropertyDataType.Integer64:
        case PropertyDataType.Floating: {
          const numericValue = formatNumeric(property.value).toString();
          return <EditStringProperty 
            propertyValue={numericValue} 
            hasChanged={property.changed} 
            hasError={property.hasError} 
            multiLine={false} 
            onChange={(value) => this.changePropertyValue(property.propertyIdOrName, value)} />;
        }
        case PropertyDataType.Boolean: {
          const booleanOptions: SelectOption[] = [
            { value: "true", label: t("boolean.true") },
            { value: "false", label: t("boolean.false") },
          ];
          const booleanOption = booleanOptions.filter((f) => f.value === property.value?.toString());
          const booleanPromise = (_: string) => Promise.resolve(booleanOptions);
          return <EditValueListProperty
            propertyValue={booleanOption}
            isMulti={false}
            hasChanged={property.changed}
            hasError={property.hasError}
            options={booleanPromise}
            onChange={(value) => this.changePropertyValue(property.propertyIdOrName, value)} />;
        }
        case PropertyDataType.Date:
        case PropertyDataType.Time:

          return <EditDateProperty 
            dateFormat={ this.getDateFormat() }
            propertyValue={ property.displayValue } 
            enableTime={ this.props.showTimeInTable ? true : false }
            hasChanged={ property.changed } 
            hasError={ property.hasError } 
            onChange={ (value) => this.changePropertyValue(property.propertyIdOrName, value) } />;

        case PropertyDataType.Lookup:
        case PropertyDataType.MultiSelectLookup: {
          const isMulti = property.dataType === PropertyDataType.MultiSelectLookup;
          const lookupSelection = this.getLookupSelection(property.value, property.displayValue);
          let lookupPromise = (_: string) => Promise.resolve(lookupSelection); //initial promise to be overwritten

          if(property.propertyIdOrName === BuiltInPropertyDef.State) {
            lookupPromise = (inputValue: string) => this.getStateLookup(inputValue);
          } else if(property.propertyIdOrName === BuiltInPropertyDef.Workflow) {
            //Workflow: keep initial promise
          } else if(property.propertyIdOrName === BuiltInPropertyDef.Class) {
            //Class: keep initial promise
          } else {
            const propertyDef = this.props.getPropertyDefinition(property.propertyIdOrName as number);

            if ( propertyDef.BasedOnValueList && propertyDef.ValueList !== null ) {
              const valueListDefinition = this.props.getValueListDefinition(propertyDef.ValueList);
              if ( valueListDefinition && valueListDefinition.RealObjectType !== true ) {
                //Valuelist
                lookupPromise = (inputValue: string) => this.getValuelistLookup(propertyDef.ValueList, inputValue);
              } else {
                //Object
                lookupPromise = (inputValue: string) => {

                  console.log( inputValue );

                  return this.getObjectLookup(propertyDef.ValueList, inputValue)
                };
              }
            }
          }
          return <EditValueListProperty
            propertyValue={lookupSelection}
            options={lookupPromise}
            isMulti={isMulti}
            hasChanged={property.changed}
            hasError={property.hasError}
            onChange={(value) => this.changePropertyValue(property.propertyIdOrName, value)} />;
        }
        default:
          //Readonly
          return(this.renderDisplayValue(property));
      }
    }
  }

  renderProperties(): ReactNode {
    const propertyArray = [];
    const { t } = this.props;
    let i = 0;
    for(const property of this.state.properties) {
      if(property.isOwnProperty) {
        const ownProperty = property.propertyIdOrName as string;
        const ownPropertyName = t(`property.${ownProperty}`);
        propertyArray.push(
          <ItemFormColumn key={i++}>
            <ItemFormColumnHeader>{ownPropertyName}:</ItemFormColumnHeader>
            <ItemFormColumnValue>{this.renderProperty(property)}</ItemFormColumnValue>
          </ItemFormColumn>
        );
      } else {
        const propertyDefinition = this.props.getPropertyDefinition(property.propertyIdOrName as number);
        propertyArray.push(
          <ItemFormColumn key={i++}>
            <ItemFormColumnHeader>{translatePropertyDefName(propertyDefinition.ID, propertyDefinition.Name)}:</ItemFormColumnHeader>
            <ItemFormColumnValue>{this.renderProperty(property)}</ItemFormColumnValue>
          </ItemFormColumn>
        );
      }
    }
    return (
      <ItemFormColumnsOuter>
        <ItemFormColumnsInner>
          {propertyArray}
        </ItemFormColumnsInner>
      </ItemFormColumnsOuter>
    );
  }

  saveForm(): void {
    return;
  }

  title(): string {
    return "";
  }

  getDateFormat(): string {
    return this.props.dateFormat + (this.props.showTimeInTable ? " " + this.props.timeFormat : "");
  }
  
  render(): ReactNode {
    const { t } = this.props;
    return (
      <div>
        <ModalContainer>
          <Draggable nodeRef={this.dragRef} handle={`.${DRAG_HANDLE_CLASSNAME}`}>
            <ItemFormContentContainer ref={this.dragRef}>
              <ItemFormHeaderContainer>
                <DragHandle>
                  <ItemFormHeader>{this.title()}</ItemFormHeader>
                </DragHandle>
              </ItemFormHeaderContainer>
              <ItemFormPropertiesContainer>
                {this.renderProperties()}
              </ItemFormPropertiesContainer>
              <ItemFormButtonRow style={{ marginTop: 20, marginBottom: 20, width: '100%' }}>
                <SecondaryButton onClick={this.props.toggleShowForm}>
                  {t("command.close")}
                </SecondaryButton>
                <GenericButton onClick={this.saveForm} style={{ marginLeft: 30 }}>
                  {t("command.save")}
                </GenericButton>
              </ItemFormButtonRow>
            </ItemFormContentContainer>
          </Draggable>
        </ModalContainer>
        <ModalBackdrop onClick={this.props.toggleShowForm}></ModalBackdrop>
      </div>
    );
  }
}
